import { useState } from 'react';
import { api, extractErrorMessage } from '../../api/client';
import type { CostosSku } from './types';
import { fmtCosto } from './formato';
import { CANDIDATOS, ComparacionCombo, FilasComponentes, FUENTE_COSTO_BASE } from './costos-comunes';
import { BuscadorSku, type SkuSugerido } from '../../components/BuscadorSku';
import { CodigoArticulo, ProveedorElegido } from '../../components/CodigoArticulo';
import { asegurarArticulos, recordarIdentidad, useIdentidades } from '../../lib/identidadArticulos';

// Module functions: the picker needs them stable.
const buscarArticulos = (texto: string) => api.get<SkuSugerido[]>(`/api/v3/catalogo/buscar?q=${encodeURIComponent(texto)}`);
const resolverArticulos = (codigo: string) => api.get<SkuSugerido[]>(`/api/v3/catalogo/buscar?codigos=${encodeURIComponent(codigo)}`);

export function CostosVistaPrevia() {
  const [sku, setSku]         = useState('');
  const [data, setData]       = useState<CostosSku | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  // The queried article (committed); the field looks up its own committed SKU.
  const identidad = useIdentidades([data?.sku]);

  /** `elegido`: the picked article's skuInterno; otherwise the typed code is resolved first (several suppliers: notice). */
  const consultar = async (elegido?: string) => {
    let s = (elegido ?? sku).trim();
    if (!s) return;
    setLoading(true);
    setError(null);
    try {
      if (!elegido) {
        const { sku: resueltos, ambiguos } = await asegurarArticulos([s], resolverArticulos);
        if (ambiguos.length > 0) { setData(null); setError(ambiguos[0]); return; }
        s = resueltos.get(s) ?? s;
      }
      setData(await api.get<CostosSku>(`/api/v3/precios/costos/${encodeURIComponent(s)}`));
    } catch (e) {
      setData(null);
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <strong>Costos (vista previa).</strong> Todavía no define el precio; sirve para elegir qué costo usar. El costo base es una propuesta.
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="w-64" onKeyDown={e => { if (e.key === 'Enter' && !e.defaultPrevented) void consultar(); }}>
          <BuscadorSku value={sku} mostrarCodigo onChange={setSku}
            onElegir={s => { recordarIdentidad(s); setSku(s.skuInterno); void consultar(s.skuInterno); }}
            buscar={buscarArticulos}
            placeholder="Código o descripción" aria-label="SKU a consultar"
            className="w-full rounded border border-slate-200 bg-white px-3 py-2 text-sm font-mono focus:border-brand-400 focus:outline-none" />
        </div>
        <button onClick={() => void consultar()} disabled={loading || !sku.trim()}
          className="rounded bg-brand-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
          {loading ? 'Consultando…' : 'Ver costos'}
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {data && (
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
              <span className="font-semibold text-slate-800"><CodigoArticulo sku={data.sku} /></span>
              <ProveedorElegido proveedor={identidad(data.sku)?.proveedor} />
              {data.descripcion && <span className="text-slate-600">{data.descripcion}</span>}
              {data.esCombo && <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700">Combo ({data.componentes.length} componentes)</span>}
              <span className="text-slate-500">IVA {data.alicuotaIva ?? '—'}%</span>
              <span className="text-slate-500">Peso {data.pesoKg != null ? `${data.pesoKg} kg` : '—'}</span>
            </div>
            {data.motivo && <p className="mt-2 text-sm text-red-600">No se pudo calcular: {data.motivo}</p>}

            <div className="mt-4 flex flex-wrap items-baseline gap-x-6 gap-y-1 rounded-lg border border-brand-100 bg-brand-50 px-4 py-3 text-sm">
              <span className="font-semibold text-slate-800">Costo base</span>
              <span className="font-mono">{fmtCosto(data.costoBase.sinIva)} <span className="text-xs text-slate-500">sin IVA</span></span>
              <span className="font-mono">{fmtCosto(data.costoBase.conIva)} <span className="text-xs text-slate-500">con IVA</span></span>
              <span className="text-xs text-slate-600">
                {data.fuenteCostoBase ? `Fuente: ${FUENTE_COSTO_BASE[data.fuenteCostoBase]}` : 'Sin costo base: se carga en la planilla de Artículos Proveedor, solapa LISTA del proveedor. Si es un combo, se resuelve completando ahí el precio de los componentes que falten o cargando el costo del combo en la solapa COMBO'}
              </span>
            </div>

            <table className="mt-4 w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2">Costo candidato</th>
                  <th className="px-3 py-2 text-right">Sin IVA</th>
                  <th className="px-3 py-2 text-right">Con IVA</th>
                  <th className="px-3 py-2">Observaciones</th>
                </tr>
              </thead>
              <tbody>
                {CANDIDATOS.map(k => {
                  const v = data.costos[k.id];
                  return (
                    <tr key={k.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-700" title={k.hint}>{k.label}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtCosto(v.sinIva)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtCosto(v.conIva)}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {v.sinDato.length > 0 && (data.esCombo ? `Sin dato en: ${v.sinDato.join(', ')}` : 'Sin dato')}
                        {k.id === 'listaConDescuentosErp' && data.descuentosErp &&
                          `Desc. proveedor ${data.descuentosErp.proveedor}% · marca ${data.descuentosErp.marca}% · SKU ${data.descuentosErp.sku}%`}
                        {k.id === 'listaConDescuentosErpContado' && data.descuentosErp &&
                          `Descuento contado ${data.descuentosErp.contado}% sobre la lista con desc. ERP`}
                        {k.id === 'listaConDescuentosErpContado' && data.esCombo && v.sinDato.length === 0 &&
                          'Cada componente con el descuento contado de su proveedor'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(data.esCombo || data.costoPlanillaCombo) && <ComparacionCombo data={data} />}

          {!data.esCombo && (
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Ofertas de proveedores</h3>
              {data.proveedorUsado && (
                <p className="mb-2 text-sm text-slate-600">
                  Se usa <strong>{data.proveedorUsado.proveedor}</strong> ({data.proveedorUsado.codigoProveedor}) —{' '}
                  {data.proveedorUsado.criterio === 'proveedor_estandar' ? 'es el proveedor estándar del SKU' : 'menor precio (el proveedor estándar no tiene oferta)'}.
                </p>
              )}
              {data.ofertas.length === 0 ? (
                <p className="text-sm text-slate-400">Sin ofertas activas con precio.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-3 py-2">Proveedor</th><th className="px-3 py-2">Código</th><th className="px-3 py-2 text-right">Precio de lista</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ofertas.map(o => {
                      const usada = data.proveedorUsado?.proveedorId === o.proveedorId && data.proveedorUsado?.codigoProveedor === o.codigoProveedor;
                      return (
                        <tr key={`${o.proveedorId}-${o.codigoProveedor}`} className={`border-t border-slate-100 ${usada ? 'bg-brand-50' : ''}`}>
                          <td className="px-3 py-2">{o.proveedor}{usada && <span className="ml-1 text-[10px] text-brand-700">en uso</span>}</td>
                          <td className="px-3 py-2 font-mono text-xs">{o.codigoProveedor}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmtCosto(o.precioUnitario)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {data.esCombo && (
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white p-4">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Componentes (costo unitario sin IVA)</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2">SKU</th><th className="px-3 py-2 text-right">Cant.</th>
                    {CANDIDATOS.map(k => <th key={k.id} className="px-3 py-2 text-right">{k.label}</th>)}
                    <th className="px-3 py-2">Proveedor</th>
                  </tr>
                </thead>
                <tbody><FilasComponentes c={data} /></tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
